import {
  Icon,
  Typography,
  Tooltip,
  Search,
  ICONS_ENUM,
  NotificationBar
} from '@sanlam-glacier/app-common/components';
import styles from './regulation-28-tab.module.scss';
import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getClientsWithReg28Breaches } from 'bff/hooks/my-practice/Regulation28ExposureApi';
import { BreakpointKey, useBreakpoint } from 'providers/breakpoint/BreakpointProvider';
import { Regulation28CardView } from '../../molecules/regulation-28-cards-view/Regulation28CardView';
import Regulation28Table from '../../molecules/regulation-28-Table/Regulation28Table';
import { Reg28ClientBreachesResponseModelMock } from '../../../types/regulation-28/regulation-28.interfaces';

const Regulation28Tab = ({ onCountChange }: { onCountChange: (count: number) => void }) => {
  const testComponentName = 'regulation28';

  const [nonCompliantClients, setNonCompliantClients] = useState<
    Reg28ClientBreachesResponseModelMock[]
  >([]);
  const [filteredData, setFilteredData] = useState<Reg28ClientBreachesResponseModelMock[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientView, setClientView] = useState<'Cards' | 'Table'>();

  const breakpointContext = useBreakpoint();

  const { data: regulation28NonCompliantClients } = useSuspenseQuery(getClientsWithReg28Breaches());

  const debouncedHandleSearch = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
    }, 500),
    []
  );

  useEffect(() => {
    const reg28BreachCount = nonCompliantClients.reduce(
      (total, client) => total + client.exposureBreaches.length,
      0
    );
    onCountChange(reg28BreachCount);

    const searchResults = nonCompliantClients.filter((row) => {
      const clientNameLower = row.clientName.toLowerCase();
      const clientID = row.clientId;

      const searchTermLower = searchTerm.toLowerCase();

      return clientNameLower.includes(searchTermLower) || clientID.includes(searchTermLower);
    });
    setFilteredData(searchResults);
  }, [searchTerm, nonCompliantClients]);

  useEffect(() => {
    setNonCompliantClients(regulation28NonCompliantClients);
    setClientView(getClientViewBasedOnBreakpoint(breakpointContext.breakpoint));
  }, [breakpointContext.breakpoint]);

  return (
    <div className={styles.container}>
      <div className={styles.headingContainer}>
        <div className={styles.heading}>
          <Typography variant={Typography.VARIANT.H5} weight={Typography.WEIGHT.MEDIUM}>
            Non-compliant clients
          </Typography>
          <Tooltip
            data-testid={`${testComponentName}Tooltip`}
            content={'TODO: Replace this with tooltip text'}
          >
            <span className={styles.helpIconContainer}>
              <Icon
                data-testid={`${testComponentName}HelpIcon`}
                icon={ICONS_ENUM.ACTION_HELP}
                className={styles.tooltipIcon}
              />
            </span>
          </Tooltip>
        </div>
        <Search
          data-testid={`${testComponentName}Search`}
          onChange={(value: string) => debouncedHandleSearch(value)}
          className={styles.searchBar}
          size={Search.SIZE.SMALL}
          placeholder={'Type in client name, number...'}
        />
      </div>
      {clientView === 'Table' ? (
        <Regulation28Table entries={filteredData} />
      ) : (
        <Regulation28CardView entries={filteredData} />
      )}

      <NotificationBar
        data-testid={`${testComponentName}NotficationBar`}
        className={styles.notificationBar}
        status={NotificationBar.STATUS.INFO}
        variant={NotificationBar.VARIANT.SECONDARY}
        highlight={false}
        roundedTop={true}
        roundedBottom={true}
        title={'Information'}
        subtitle={
          <>
            <p>
              We monitor each investment to ensure that it complies with Regulation 28 over a 12
              month period. This report indicates all accounts that are in breach of Reg 28.
            </p>
            <p>
              Should the investment remain non-compliant for 360 consecutive days (12 months),
              Glacier will switch the client`s investment to achieve compliance.
            </p>
            <p>
              The monitoring process is applicable to all investments that were issued on or after 1
              April 2011, as well as all existing investments where a transaction was processed
              after 1 July 2011.
            </p>
          </>
        }
      />
    </div>
  );
};

export default Regulation28Tab;

const getClientViewBasedOnBreakpoint = (breakpoint: BreakpointKey) => {
  if (breakpoint === 'md' || breakpoint === 'sm') return 'Cards';
  return 'Table';
};
